import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Reveal } from "@/components/demo/lib/Reveal";
import { useCounter } from "@/components/demo/lib/useCounter";
import { useReveal } from "@/components/demo/lib/useReveal";
import { IMG } from "@/components/demo/lib/images";

const WA = "41765008176";

const TIMELINE = [
  { y: "1987", t: "Fondazione dello studio", d: "L'Avv. Bianchi senior apre lo studio in Via Nassa, focalizzato sul diritto societario per le aziende ticinesi." },
  { y: "2002", t: "Espansione M&A", d: "Prima operazione cross-border da CHF 240 milioni. Apertura del desk di Milano." },
  { y: "2011", t: "Partnership internazionale", d: "Ingresso nel network Lex Mundi: assistenza a clienti in 18 giurisdizioni." },
  { y: "2019", t: "Tax & Wealth", d: "Nasce il dipartimento Wealth Planning dedicato a UHNWI e family office svizzeri e internazionali." },
  { y: "2026", t: "Oggi", d: "23 professionisti, sedi a Lugano, Zurigo e Singapore. Patrimoni assistiti per oltre CHF 4,2 miliardi." },
];

const PRACTICE = [
  { n: "01", t: "Diritto societario & M&A", d: "Operazioni straordinarie, due diligence, governance per gruppi familiari e PMI svizzere ed estere." },
  { n: "02", t: "Tutela patrimoniale", d: "Strutturazione patrimoniale, trust, fondazioni di famiglia e passaggio generazionale." },
  { n: "03", t: "Fiscalità internazionale", d: "Tax ruling, transfer pricing e ottimizzazione fiscale cross-border per UHNWI e gruppi multinazionali." },
  { n: "04", t: "Contenzioso & arbitrato", d: "Difesa giudiziale e stragiudiziale dinanzi a corti svizzere e tribunali arbitrali internazionali." },
  { n: "05", t: "Banking & Finance", d: "Regolamentazione finanziaria, compliance FINMA e supporto a istituti bancari e gestori patrimoniali." },
  { n: "06", t: "Immigration & Residency", d: "Permessi B/C, imposizione forfettaria e relocation per imprenditori e sportivi internazionali." },
];

const ATTORNEYS = [
  { n: "Roberto Bianchi", r: "Managing Partner", s: "Corporate · M&A", e: "LL.M. Harvard · ETH Zurich" },
  { n: "Caterina Bianchi", r: "Partner", s: "Tutela Patrimoniale", e: "MAS Tax · Lic. iur. USI" },
  { n: "Lorenzo Maggi", r: "Partner", s: "Tax & Finance", e: "LL.M. NYU · Avv." },
  { n: "Sofia Rüegg", r: "Counsel", s: "Contenzioso", e: "Dr. iur. UZH · Avv." },
  { n: "Marc Aubry", r: "Counsel", s: "Banking", e: "LL.M. Cambridge" },
  { n: "Wei Chen", r: "Partner Singapore", s: "Asia Desk", e: "LL.M. NUS · Avv." },
];

const FAQ = [
  { q: "Il primo colloquio è gratuito?", a: "Sì. Il primo incontro conoscitivo, in studio o in videocall, è gratuito e soggetto al segreto professionale ex art. 321 CP." },
  { q: "Lavorate solo con grandi clienti?", a: "Assistiamo famiglie imprenditoriali, PMI svizzere e clienti privati di fascia alta. Selezioniamo i mandati per dedicare a ciascuno l'attenzione che merita." },
  { q: "Operate anche all'estero?", a: "Sì. Grazie al network Lex Mundi seguiamo operazioni in 18 giurisdizioni. Le sedi proprie di Zurigo e Singapore coprono il mercato DACH e asiatico." },
  { q: "Come vengono fatturati i mandati?", a: "Offriamo onorari orari, fee fisse per pratiche standard o success fee su operazioni straordinarie. Tutto definito in modo trasparente in fase di mandato." },
];

function Counter({ v, suffix = "", prefix = "", label }: { v: number; suffix?: string; prefix?: string; label: string }) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const c = useCounter(v, visible);
  return (
    <div ref={ref}>
      <div className="font-serif text-5xl sm:text-6xl text-[#c9a449] tabular-nums">{prefix}{Math.round(c).toLocaleString("it-CH")}{suffix}</div>
      <div className="text-[10px] tracking-[0.35em] uppercase text-white/40 mt-3">{label}</div>
    </div>
  );
}

function Bianchi() {
  const [drawer, setDrawer] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-[#0b1428] text-stone-100 antialiased overflow-x-hidden">
      <style>{`
        .ff-serif { font-family: 'Cormorant Garamond', 'Times New Roman', serif }
        body { font-family: 'Inter', sans-serif }
      `}</style>

      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0b1428]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-10 py-4 sm:py-5 flex lg:grid lg:grid-cols-3 items-center justify-between gap-3">
          <div className="ff-serif text-xl sm:text-2xl tracking-wide whitespace-nowrap">Bianchi <span className="text-[#c9a449]">&</span> Partners</div>
          <nav className="hidden lg:flex justify-center gap-9 text-[11px] tracking-[0.3em] uppercase text-white/70">
            <a href="#practice" className="hover:text-[#c9a449]">Aree di pratica</a>
            <a href="#attorneys" className="hover:text-[#c9a449]">Avvocati</a>
            <a href="#timeline" className="hover:text-[#c9a449]">Heritage</a>
            <a href="#faq" className="hover:text-[#c9a449]">FAQ</a>
          </nav>
          <div className="lg:text-right shrink-0">
            <button onClick={() => setDrawer(true)} className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.3em] uppercase border border-[#c9a449]/60 text-[#c9a449] px-3 sm:px-5 py-2 sm:py-2.5 hover:bg-[#c9a449] hover:text-[#0b1428] transition whitespace-nowrap">
              <span className="sm:hidden">Contatti</span><span className="hidden sm:inline">Consulenza riservata</span>
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-32 pb-20 px-6 sm:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.law.hero} alt="Sala riunioni dello studio Bianchi & Partners" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0b1428] via-[#0b1428]/85 to-[#0b1428]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full grid lg:grid-cols-[3fr_2fr] gap-16 items-end">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#c9a449] mb-8">— Fondato nel 1987 · Lugano · Zurigo · Singapore</div>
            <h1 className="ff-serif text-5xl sm:text-8xl lg:text-[8.5vw] leading-[0.95] break-words">
              Consulenza legale<br /><em className="text-[#c9a449]">strategica.</em>
            </h1>
          </div>
          <Reveal delay={200}>
            <div className="border-l-2 border-[#c9a449] pl-8">
              <p className="text-white/70 leading-relaxed text-lg">
                Studio legale internazionale al servizio di famiglie imprenditoriali, fondi d'investimento e family office. Tre generazioni di consulenza giuridica al fianco delle eccellenze svizzere.
              </p>
              <a href="#practice" className="mt-8 inline-block text-[11px] tracking-[0.3em] uppercase text-[#c9a449] border-b border-[#c9a449] pb-1 hover:text-white hover:border-white transition">Scopri le nostre aree di pratica →</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10 px-6 sm:px-10 py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10">
          <Counter v={38} label="Anni di esperienza" />
          <Counter v={4.2} suffix=" bn" prefix="CHF " label="Patrimoni assistiti" />
          <Counter v={23} label="Professionisti" />
          <Counter v={18} label="Giurisdizioni" />
        </div>
      </section>

      {/* PRACTICE */}
      <section id="practice" className="px-6 sm:px-10 py-32">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#c9a449] mb-4">I. Aree di pratica</div>
            <h2 className="ff-serif text-5xl lg:text-7xl mb-20 max-w-3xl leading-tight break-words">Sei aree di eccellenza,<br />un'unica <em className="text-[#c9a449]">deontologia.</em></h2>
          </Reveal>
          <div className="space-y-px">
            {PRACTICE.map((p, i) => (
              <Reveal key={p.n} delay={i * 40}>
                <div className="grid lg:grid-cols-[120px_1fr_2fr_auto] gap-6 py-8 items-baseline border-t border-white/10 group hover:bg-white/[0.02] px-2 transition">
                  <div className="ff-serif text-2xl text-[#c9a449]">{p.n}</div>
                  <div className="ff-serif text-2xl lg:text-3xl">{p.t}</div>
                  <div className="text-sm text-white/60 leading-relaxed">{p.d}</div>
                  <a href="#" className="text-[10px] tracking-[0.3em] uppercase text-[#c9a449] opacity-0 group-hover:opacity-100 transition whitespace-nowrap">Approfondisci →</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section id="timeline" className="px-6 sm:px-10 py-32 bg-[#080f1f] border-y border-white/10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#c9a449] mb-4">II. La nostra storia</div>
            <h2 className="ff-serif text-5xl lg:text-7xl mb-20 break-words">Tre generazioni<br />di <em className="text-[#c9a449]">consulenza.</em></h2>
          </Reveal>
          <div className="relative">
            <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-[#c9a449]/30" />
            <div className="space-y-16">
              {TIMELINE.map((e, i) => (
                <Reveal key={e.y} delay={i * 80}>
                  <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
                    <div className={`pl-12 lg:pl-0 ${i % 2 ? "lg:text-left lg:[direction:ltr]" : "lg:text-right"} relative`}>
                      <div className="absolute left-2 lg:left-auto lg:right-0 top-2 h-3 w-3 rounded-full bg-[#c9a449]" style={i % 2 ? { left: 8, right: "auto" } : {}} />
                      <div className="ff-serif text-6xl text-[#c9a449]">{e.y}</div>
                      <div className="ff-serif text-2xl mt-2">{e.t}</div>
                    </div>
                    <div className={`pl-12 lg:pl-12 ${i % 2 ? "lg:[direction:ltr]" : ""}`}>
                      <p className="text-white/60 leading-relaxed max-w-md">{e.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ATTORNEYS */}
      <section id="attorneys" className="px-6 sm:px-10 py-32">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="ff-serif text-5xl lg:text-7xl mb-16 break-words">Partner & <em className="text-[#c9a449]">Counsel</em></h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {ATTORNEYS.map((a, i) => (
              <Reveal key={a.n} delay={i * 40}>
                <div className="bg-[#0b1428] p-8 h-full group hover:bg-[#0e1b35] transition">
                  <div className="h-20 w-20 rounded-full border border-[#c9a449]/40 grid place-items-center ff-serif text-3xl text-[#c9a449] mb-6">{a.n.split(" ").map(p => p[0]).join("")}</div>
                  <div className="ff-serif text-2xl">{a.n}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#c9a449] mt-1">{a.r}</div>
                  <div className="text-sm text-white/60 mt-4">{a.s}</div>
                  <div className="text-[10px] text-white/40 mt-2 tracking-wider">{a.e}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="px-6 sm:px-10 py-28 border-y border-white/10 bg-[#080f1f]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="ff-serif text-[120px] leading-none text-[#c9a449]/40">"</div>
          <blockquote className="ff-serif text-2xl lg:text-4xl leading-snug -mt-6 break-words">
            Lo studio Bianchi ci ha seguiti nel passaggio generazionale del nostro gruppo industriale. <em className="text-[#c9a449]">Discrezione totale e una visione strategica che è andata ben oltre il puro aspetto legale.</em>
          </blockquote>
          <div className="mt-8 text-[10px] tracking-[0.35em] uppercase text-white/50">— Famiglia M., gruppo industriale del Mendrisiotto · Cliente dal 2014</div>
        </div>
      </section>

      {/* MEDIA */}
      <section className="px-6 sm:px-10 py-20 border-b border-white/10 bg-[#080f1f]">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#c9a449] mb-8">— Hanno parlato di noi</div>
          <div className="flex flex-wrap gap-x-14 gap-y-6 ff-serif text-3xl text-white/30">
            {["Financial Times", "Bilan", "NZZ", "Wall Street Journal", "Handelsblatt", "Il Sole 24 Ore"].map((p) => <span key={p}>{p}</span>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 sm:px-10 py-32">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#c9a449] mb-4">III. Domande frequenti</div>
            <h2 className="ff-serif text-5xl lg:text-6xl mb-12 break-words">Risposte<br /><em className="text-[#c9a449]">chiare.</em></h2>
          </Reveal>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <button key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left border border-white/10 hover:border-[#c9a449]/40 transition p-6 block">
                <div className="flex justify-between items-center gap-4">
                  <div className="ff-serif text-xl">{f.q}</div>
                  <div className={`text-2xl text-[#c9a449] transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</div>
                </div>
                <div className={`overflow-hidden transition-all ${openFaq === i ? "max-h-40 mt-4" : "max-h-0"}`}>
                  <p className="text-white/60 leading-relaxed">{f.a}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-10 py-32 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="ff-serif text-5xl lg:text-7xl leading-tight">Una conversazione<br /><em className="text-[#c9a449]">riservata.</em></div>
            <p className="text-white/60 mt-8 max-w-xl mx-auto">Primo colloquio gratuito, soggetto a segreto professionale. Risposta entro 24 ore lavorative.</p>
            <button onClick={() => setDrawer(true)} className="mt-12 bg-[#c9a449] text-[#0b1428] px-10 py-5 text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-white transition">Richiedi consulenza</button>
          </Reveal>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-10 border-t border-white/10 text-xs text-white/40">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between gap-6 tracking-wider">
          <div>BIANCHI & PARTNERS · Via Nassa 5, 6900 Lugano · Iscritti all'Ordine degli Avvocati del Canton Ticino</div>
          <div>Demo FlashWeb · <Link to="/" className="underline">flash-web.ch</Link></div>
        </div>
      </footer>

      {/* DRAWER */}
      {drawer && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm" onClick={() => setDrawer(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0b1428] border-l border-[#c9a449]/30 p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-10">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#c9a449]">Consulenza riservata</div>
                <div className="ff-serif text-3xl mt-2">Contattaci in privato</div>
              </div>
              <button onClick={() => setDrawer(false)} className="text-white/50 hover:text-white text-2xl">×</button>
            </div>
            <p className="text-white/60 text-sm mb-8">Tutte le comunicazioni sono soggette a segreto professionale ex art. 321 CP.</p>
            <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Buongiorno, vorrei richiedere una consulenza riservata con lo studio Bianchi & Partners.")}`} className="block text-center bg-[#c9a449] text-[#0b1428] py-4 text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-white transition mb-3">WhatsApp riservato</a>
            <a href="tel:+41912100100" className="block text-center border border-[#c9a449]/40 py-4 text-[11px] tracking-[0.3em] uppercase text-[#c9a449] hover:bg-[#c9a449] hover:text-[#0b1428] transition">+41 91 210 01 00</a>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/demo/bianchi-partners")({
  head: () => ({
    meta: [
      { title: "Bianchi & Partners — Studio legale a Lugano (Demo FlashWeb)" },
      { name: "description", content: "Studio legale internazionale a Lugano: diritto societario, tutela patrimoniale, fiscalità e contenzioso. Consulenza riservata su appuntamento." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: Bianchi,
});
